import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { PaymentAuthorizationView, RequestOtpResultView } from '@solar/contracts';

import { Alert } from '../../../../components/Alert';
import { Button } from '../../../../components/Button';
import { PageSpinner } from '../../../../components/PageSpinner';
import portalStyles from '../../../../components/PortalComponents.module.css';
import { StatusBadge } from '../../../../components/StatusBadge';
import { billingApi } from '../../../../features/billing/billing.api';
import { useSessionQuery as useQuery } from '../../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatBusinessDate } from '../../../../lib/format-date';
import { paths } from '../../../../routes/paths';
import styles from '../../AppPages.module.css';

function secondsRemaining(until: string | null): number {
  if (until === null) {
    return 0;
  }

  return Math.max(0, Math.ceil((new Date(until).getTime() - Date.now()) / 1_000));
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function canRequestOtp(authorization: PaymentAuthorizationView): boolean {
  return (
    authorization.mandateActive &&
    (authorization.status === 'CREATED' ||
      authorization.status === 'LINK_OPENED' ||
      authorization.status === 'OTP_EXPIRED')
  );
}

function canEnterOtp(authorization: PaymentAuthorizationView): boolean {
  return (
    authorization.mandateActive &&
    (authorization.status === 'OTP_SENT' || authorization.status === 'OTP_REQUESTED')
  );
}

function isComplete(authorization: PaymentAuthorizationView): boolean {
  return authorization.status === 'COMPLETED' || authorization.paymentStatus === 'SUCCEEDED';
}

function useOtpCountdown(expiresAt: string | null): number {
  const [remaining, setRemaining] = useState(() => secondsRemaining(expiresAt));

  useEffect(() => {
    setRemaining(secondsRemaining(expiresAt));

    if (expiresAt === null) {
      return;
    }

    const timer = window.setInterval(() => {
      setRemaining(secondsRemaining(expiresAt));
    }, 1_000);

    return () => {
      window.clearInterval(timer);
    };
  }, [expiresAt]);

  return remaining;
}

function useResendCooldown(cooldownSeconds: number, lastResendAt: string | null): number {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (lastResendAt === null || cooldownSeconds <= 0) {
      setRemaining(0);
      return;
    }

    const elapsed = Math.floor((Date.now() - new Date(lastResendAt).getTime()) / 1_000);
    const initial = Math.max(0, cooldownSeconds - elapsed);
    setRemaining(initial);

    if (initial <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setRemaining((current) => Math.max(0, current - 1));
    }, 1_000);

    return () => {
      window.clearInterval(timer);
    };
  }, [cooldownSeconds, lastResendAt]);

  return remaining;
}

export function PaymentAuthorizePage(): React.JSX.Element {
  const { authorizationId: routeAuthorizationId } = useParams<{ authorizationId?: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const scheduleId = searchParams.get('scheduleId');
  const queryClient = useQueryClient();
  const otpInputId = useId();
  const otpInputRef = useRef<HTMLInputElement>(null);

  const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(60);
  const [lastResendAt, setLastResendAt] = useState<string | null>(null);
  const [maskedDestination, setMaskedDestination] = useState<string | null>(null);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'submitting' | 'submitted'>('idle');

  const resolvedAuthorizationId = routeAuthorizationId ?? '';

  const authorization = useQuery({
    queryKey: ['customer', 'payment-authorizations', resolvedAuthorizationId, token, scheduleId],
    queryFn: async (): Promise<PaymentAuthorizationView> => {
      if (token !== null && token !== '') {
        return billingApi.resolvePaymentAuthorization(token);
      }

      if (resolvedAuthorizationId !== '') {
        return billingApi.getPaymentAuthorization(resolvedAuthorizationId);
      }

      if (scheduleId !== null && scheduleId !== '') {
        const repayment = await billingApi.getRepayment(scheduleId);
        const authId = repayment.activeAuthorizationSessionId ?? repayment.activePaymentAttemptId;

        if (authId === null) {
          throw new Error('No active payment authorization for this instalment.');
        }

        return billingApi.getPaymentAuthorization(authId);
      }

      throw new Error('Missing authorization reference.');
    },
    enabled: token !== null || resolvedAuthorizationId !== '' || scheduleId !== null,
  });

  const authorizationId = authorization.data?.id ?? resolvedAuthorizationId;

  const invalidate = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['customer', 'payment-authorizations', resolvedAuthorizationId, token, scheduleId],
    });
  }, [queryClient, resolvedAuthorizationId, scheduleId, token]);

  const applyOtpResult = (result: RequestOtpResultView): void => {
    setOtpExpiresAt(result.otpExpiresAt);
    setResendCooldownSeconds(result.resendCooldownSeconds);
    setLastResendAt(new Date().toISOString());
    setMaskedDestination(result.maskedEmailDestination);
  };

  const requestOtp = useMutation({
    mutationFn: () => billingApi.requestOtp(authorizationId),
    onSuccess: (result) => {
      applyOtpResult(result);
      void invalidate();
      otpInputRef.current?.focus();
    },
  });

  const resendOtp = useMutation({
    mutationFn: () => billingApi.resendOtp(authorizationId),
    onSuccess: (result) => {
      applyOtpResult(result);
      void invalidate();
      otpInputRef.current?.focus();
    },
  });

  const submitOtp = useMutation({
    mutationFn: (otp: string) => billingApi.submitOtp(authorizationId, { otp }),
    onMutate: () => {
      setSubmitPhase('submitting');
    },
    onSuccess: async () => {
      setSubmitPhase('submitted');
      if (otpInputRef.current !== null) {
        otpInputRef.current.value = '';
      }
      await invalidate();
    },
    onError: () => {
      setSubmitPhase('idle');
      if (otpInputRef.current !== null) {
        otpInputRef.current.value = '';
      }
    },
  });

  const otpCountdown = useOtpCountdown(otpExpiresAt ?? authorization.data?.otpExpiresAt ?? null);
  const resendCountdown = useResendCooldown(
    resendCooldownSeconds,
    lastResendAt ?? authorization.data?.lastResendAt ?? null,
  );

  useEffect(() => {
    if (
      authorization.data?.otpExpiresAt !== null &&
      authorization.data?.otpExpiresAt !== undefined
    ) {
      setOtpExpiresAt(authorization.data.otpExpiresAt);
    }

    if (authorization.data?.maskedEmailDestination !== null) {
      setMaskedDestination(authorization.data?.maskedEmailDestination ?? null);
    }
  }, [authorization.data]);

  const handleSubmitOtp = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const rawOtp = formData.get('otp');
    const otp = typeof rawOtp === 'string' ? rawOtp.trim() : '';

    if (otp === '') {
      return;
    }

    submitOtp.mutate(otp);
    event.currentTarget.reset();
  };

  if (authorization.isPending) {
    return <PageSpinner label="Loading payment authorization" />;
  }

  if (authorization.isError) {
    return <Alert tone="error">{toDisplayMessage(authorization.error)}</Alert>;
  }

  const data = authorization.data;
  const showRequestOtp = canRequestOtp(data);
  const showOtpForm = canEnterOtp(data) && !isComplete(data);
  const otpExpired = showOtpForm && otpCountdown <= 0 && data.otpExpiresAt !== null;
  const destination = maskedDestination ?? data.maskedEmailDestination;
  const mutationError = requestOtp.error ?? resendOtp.error ?? submitOtp.error ?? null;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Authorize instalment payment</h1>
          <p className={styles.pageSubtitle}>
            Review the instalment details and complete the secure authorization steps.
          </p>
        </div>
        <StatusBadge status={data.status} />
      </header>

      <p className={portalStyles.backLinkRow}>
        <Link className={portalStyles.backLink} to={paths.customerRepayments}>
          Back to repayments
        </Link>
      </p>

      {mutationError !== null && <Alert tone="error">{toDisplayMessage(mutationError)}</Alert>}
      {submitOtp.isSuccess && (
        <Alert tone="success">Payment submitted. We will confirm once processing completes.</Alert>
      )}
      {isComplete(data) && (
        <Alert tone="success">This instalment payment has been authorized successfully.</Alert>
      )}

      {!data.mandateActive && (
        <Alert tone="error">
          Your Direct Debit mandate is not active. Contact support before authorizing this payment.
        </Alert>
      )}

      <section
        className={`${styles.card} ${portalStyles.heroBanner} ${portalStyles.amountHighlightCard}`}
        aria-labelledby="authorize-amount-heading"
      >
        <div className={portalStyles.heroBannerContent}>
          <p className={`${portalStyles.heroBannerEyebrow} ${portalStyles.amountHighlightEyebrow}`}>
            Instalment due
          </p>
          <h2 className={portalStyles.heroBannerTitle} id="authorize-amount-heading">
            {formatNgn(data.instalmentAmount)}
          </h2>
          <p className={portalStyles.heroBannerDescription}>
            Due {formatBusinessDate(data.dueDate)}
          </p>
        </div>
      </section>

      <section className={styles.card} aria-labelledby="authorize-details-heading">
        <h2 className={styles.cardTitle} id="authorize-details-heading">
          Payment details
        </h2>

        <dl className={styles.definitionList}>
          <dt>Contract</dt>
          <dd>{data.contractReference ?? data.subscriptionId.slice(0, 8)}</dd>
          <dt>Product</dt>
          <dd>{data.productName ?? '—'}</dd>
          <dt>Due date</dt>
          <dd>{formatBusinessDate(data.dueDate)}</dd>
          <dt>Amount</dt>
          <dd>{formatNgn(data.instalmentAmount)}</dd>
          <dt>Bank</dt>
          <dd>{data.bankName ?? '—'}</dd>
          <dt>Account</dt>
          <dd>{data.accountMasked ?? '—'}</dd>
          <dt>Payment status</dt>
          <dd>
            <StatusBadge status={data.paymentStatus} />
          </dd>
        </dl>
      </section>

      {showRequestOtp && (
        <section className={styles.card} aria-labelledby="request-otp-heading">
          <h2 className={styles.cardTitle} id="request-otp-heading">
            Step 1 — Request OTP
          </h2>
          <p>
            Click below to request a one-time password by email. You will enter it on the next step
            to authorize this debit.
          </p>
          <div className={styles.actions}>
            <Button busy={requestOtp.isPending} onClick={() => requestOtp.mutate()}>
              Request OTP
            </Button>
          </div>
        </section>
      )}

      {showOtpForm && (
        <section className={styles.card} aria-labelledby="enter-otp-heading">
          <h2 className={styles.cardTitle} id="enter-otp-heading">
            Step 2 — Enter OTP
          </h2>

          {destination !== null ? (
            <p>
              A one-time password was sent to <strong>{destination}</strong>.
            </p>
          ) : (
            <p>
              TransactPay has accepted the OTP request. Check the email associated with your
              direct-debit mandate.
            </p>
          )}

          <Alert tone="info">
            Check your Inbox and allow a few minutes for delivery. If the code is not there, check
            Spam or Junk. Use Resend OTP below when the cooldown finishes.
          </Alert>

          {data.otpExpiresAt !== null && !otpExpired && (
            <p aria-live="polite">
              OTP expires in <strong>{formatCountdown(otpCountdown)}</strong>
            </p>
          )}

          {otpExpired && (
            <Alert tone="error">
              This OTP has expired. Request a new code using the button below.
            </Alert>
          )}

          <form onSubmit={handleSubmitOtp} noValidate>
            <div className={styles.filters}>
              <label htmlFor={otpInputId}>
                One-time password
                <input
                  ref={otpInputRef}
                  id={otpInputId}
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={8}
                  required
                  disabled={otpExpired || submitPhase !== 'idle'}
                  aria-describedby={`${otpInputId}-hint`}
                />
              </label>
            </div>
            <p className={styles.pageSubtitle} id={`${otpInputId}-hint`}>
              Enter the code from your email. It is not stored after you submit.
            </p>

            <div className={styles.actions}>
              <Button
                type="submit"
                busy={submitOtp.isPending}
                disabled={otpExpired || submitPhase === 'submitted'}
              >
                Submit OTP and pay
              </Button>
              <Button
                type="button"
                variant="secondary"
                busy={resendOtp.isPending}
                disabled={resendCountdown > 0 || submitPhase === 'submitted'}
                onClick={() => resendOtp.mutate()}
              >
                {resendCountdown > 0 ? `Resend OTP (${resendCountdown}s)` : 'Resend OTP'}
              </Button>
            </div>
          </form>
        </section>
      )}

      {data.status === 'OTP_EXPIRED' && !showOtpForm && (
        <section className={styles.card} aria-labelledby="expired-otp-heading">
          <h2 className={styles.cardTitle} id="expired-otp-heading">
            OTP expired
          </h2>
          <p>Your previous OTP has expired. Request a new code to continue.</p>
          <div className={styles.actions}>
            <Button busy={requestOtp.isPending} onClick={() => requestOtp.mutate()}>
              Send new OTP
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
